import {BiomarkerRule, RecommendationType, RiskTier, RuleSource, RuleType} from './types';
import type {
    BundledPanelGroup,
    CustomLabPackage,
    MatchedMarker,
} from './services/bioMatchEnrollmentService';

export const formatRuleTypeLabel = (ruleType: RuleType): string => {
    switch (ruleType) {
        case RuleType.GREATER_THAN:
            return 'Greater Than';
        case RuleType.GREATER_THAN_OR_EQUAL:
            return 'Greater Than or Equal';
        case RuleType.LESS_THAN:
            return 'Less Than';
        case RuleType.LESS_THAN_OR_EQUAL:
            return 'Less Than or Equal';
        case RuleType.RANGE:
            return 'Range';
        case RuleType.OUTSIDE_RANGE:
            return 'Outside Range';
        case RuleType.EQUALS:
            return 'Equals';
        case RuleType.NOT_EQUALS:
            return 'Not Equals';
        default:
            return ruleType;
    }
};

export const formatRecommendationTypeLabel = (type: RecommendationType): string => {
    switch (type) {
        case RecommendationType.SUPPLEMENT:
            return 'Supplement';
        case RecommendationType.PROGRAM:
            return 'Care Program';
        case RecommendationType.CARE_PATHWAY:
            return 'Care Pathway';
        case RecommendationType.REFERRAL:
            return 'Urgent Referral';
        case RecommendationType.LIFESTYLE:
            return 'Lifestyle Option';
        default:
            return type;
    }
};

export const formatRiskTierLabel = (tier: RiskTier): string => {
    switch (tier) {
        case RiskTier.CRITICAL:
            return 'Critical';
        case RiskTier.HIGH:
            return 'High';
        case RiskTier.MODERATE:
            return 'Moderate';
        case RiskTier.LOW:
            return 'Low';
        default:
            return tier;
    }
};

export const requiresRangeInputs = (ruleType: RuleType): boolean => {
    return ruleType === RuleType.RANGE || ruleType === RuleType.OUTSIDE_RANGE;
};

export const createEmptyRule = (groupIndex = 0): BiomarkerRule => ({
    test_type: '',
    assay_code: null,
    rule_type: RuleType.GREATER_THAN,
    group_index: groupIndex,
    rule_source: RuleSource.BIOMARKER,
    measurement_key: null,
    min_value: null,
    max_value: null,
    threshold_value: null,
    rule_expression: '',
    gender: null,
});

/**
 * Per-source UI metadata. The modal exposes a single "Source" dropdown
 * whose options collapse `rule_source` and (for biometrics) the
 * canonical `measurement_key` into one selection. Adding a new
 * biometric source = one more entry here.
 */
export interface RuleSourceOption {
    /** Stable id used as the <select> value. */
    id: string;
    /** Human label shown in the dropdown. */
    label: string;
    rule_source: RuleSource;
    measurement_key: string | null;
    /** Auto-populated `test_type` so the row remains self-describing
     * even though there's no biomarker autocomplete for non-biomarker
     * sources. The BE preserves this string for display. */
    test_type: string;
}

export const RULE_SOURCE_OPTIONS: RuleSourceOption[] = [
    {id: 'biomarker', label: 'Lab Biomarker', rule_source: RuleSource.BIOMARKER, measurement_key: null, test_type: ''},
    {id: 'bmi', label: 'BMI', rule_source: RuleSource.BIOMETRIC, measurement_key: 'bmi', test_type: 'BMI'},
    {
        id: 'waist',
        label: 'Waist Circumference',
        rule_source: RuleSource.BIOMETRIC,
        measurement_key: 'waist',
        test_type: 'Waist Circumference',
    },
    {
        id: 'bp_systolic',
        label: 'Systolic BP',
        rule_source: RuleSource.BIOMETRIC,
        measurement_key: 'bp_systolic',
        test_type: 'Systolic BP',
    },
    {
        id: 'bp_diastolic',
        label: 'Diastolic BP',
        rule_source: RuleSource.BIOMETRIC,
        measurement_key: 'bp_diastolic',
        test_type: 'Diastolic BP',
    },
    {id: 'age', label: 'Age (years)', rule_source: RuleSource.AGE, measurement_key: null, test_type: 'Age'},
];

export const getRuleSourceOptionId = (rule: BiomarkerRule): string => {
    const source = rule.rule_source ?? RuleSource.BIOMARKER;
    if (source === RuleSource.BIOMARKER) return 'biomarker';
    if (source === RuleSource.AGE) return 'age';
    // biometric
    const match = RULE_SOURCE_OPTIONS.find(
        (o) => o.rule_source === RuleSource.BIOMETRIC && o.measurement_key === rule.measurement_key
    );
    return match?.id ?? 'biomarker';
};

/**
 * Field visibility matrix per recommendation_type, from Section 2.1.1
 * of the developer spec.
 */
const FIELD_VISIBILITY: Record<string, ReadonlyArray<RecommendationType>> = {
    price: [RecommendationType.SUPPLEMENT, RecommendationType.PROGRAM, RecommendationType.LIFESTYLE],
    members_price: [RecommendationType.SUPPLEMENT, RecommendationType.PROGRAM, RecommendationType.LIFESTYLE],
    kit_sku: [RecommendationType.PROGRAM],
    pathway_url: [RecommendationType.CARE_PATHWAY],
    track_enrollment: [RecommendationType.PROGRAM, RecommendationType.LIFESTYLE],
};

export const isFieldVisible = (field: keyof typeof FIELD_VISIBILITY, type: RecommendationType): boolean => {
    return FIELD_VISIBILITY[field]?.includes(type) ?? true;
};

/**
 * Group rules by their `group_index` for display. Returns groups in
 * ascending group_index order, each containing the rules that share
 * that index. Rules within a group keep their original array index so
 * onChange/onRemove handlers can still address them.
 */
export const groupRulesByIndex = (
    rules: BiomarkerRule[]
): Array<{groupIndex: number; rules: Array<{rule: BiomarkerRule; flatIndex: number}>}> => {
    const buckets = new Map<number, Array<{rule: BiomarkerRule; flatIndex: number}>>();
    rules.forEach((rule, flatIndex) => {
        const idx = rule.group_index ?? 0;
        const bucket = buckets.get(idx);
        const entry = {rule, flatIndex};
        if (bucket) bucket.push(entry);
        else buckets.set(idx, [entry]);
    });
    return Array.from(buckets.entries())
        .sort(([a], [b]) => a - b)
        .map(([groupIndex, items]) => ({groupIndex, rules: items}));
};

/**
 * New API responses carry matched_markers independently of ordered assays.
 * Fall back only when the field is absent, preserving compatibility during a
 * rolling deploy without overriding an intentionally empty marker list.
 */
export function resolveDisplayMatchedMarkers(pkg: CustomLabPackage): MatchedMarker[] {
    if (pkg.matched_markers !== undefined) return pkg.matched_markers;

    return pkg.assays
        .filter((assay) => !assay.is_calculated)
        .map((assay) => ({
            code: assay.code,
            name: assay.name,
            loinc: assay.loinc,
            sourced_from: assay.sourced_from,
            bundled_panel_code: assay.bundled_panel_code,
        }));
}

/**
 * Panels can produce abnormal markers that were not themselves recommendation
 * rules (calculated Cholesterol/HDL Ratio is the canonical example). Add those
 * for display when the dashboard's lab classifier says they are abnormal,
 * without adding them to `pkg.assays` or any checkout payload.
 */
export function addAbnormalPanelMarkers(
    markers: MatchedMarker[],
    panels: BundledPanelGroup[],
    isAbnormal: (marker: {code: string; name: string; loinc: string | null}) => boolean,
): MatchedMarker[] {
    const result = new Map(markers.map((marker) => [marker.code, marker]));

    for (const panel of panels) {
        for (const candidate of panel.included_markers ?? []) {
            if (result.has(candidate.code) || !isAbnormal(candidate)) continue;
            result.set(candidate.code, {
                ...candidate,
                sourced_from: [],
                bundled_panel_code: panel.code,
            });
        }
    }

    const panelOrder = new Map(panels.map((panel, index) => [panel.code, index]));
    return [...result.values()].sort((a, b) => {
        const aOrder = a.bundled_panel_code
            ? (panelOrder.get(a.bundled_panel_code) ?? Number.MAX_SAFE_INTEGER - 1)
            : Number.MAX_SAFE_INTEGER;
        const bOrder = b.bundled_panel_code
            ? (panelOrder.get(b.bundled_panel_code) ?? Number.MAX_SAFE_INTEGER - 1)
            : Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
    });
}
